import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { ButtonTypeEnum } from '@panaya/ui/p-button';
import { TranslateService } from '@ngx-translate/core';
import { ListItem } from '../p-field/p-field-single-select';

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_GROUPING_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE_OPTIONS = [
    { name: '25', id: 0 },
    { name: '50', id: 1 },
    { name: '100', id: 2 },
    { name: '1000', id: 3 }
];

@Component({
    selector: 'p-paginator',
    template: `
        <div class="p-paginator" automation-id="grid-p-paginator">
            <div class="paginator-left-side">
                <p-button
                    class="p-paginator-arrow first"
                    ariaLabel="Move to the first page"
                    [disabled]="!canMoveToPrevious"
                    iconBefore="first"
                    iconBeforeSize="xs"
                    [buttonType]="buttonType.IsIcon"
                    (buttonClicked)="firstPageClicked.emit()"
                ></p-button>
                <p-button
                    class="p-paginator-arrow prev"
                    ariaLabel="Move to the previous page"
                    [disabled]="!canMoveToPrevious"
                    iconBefore="chevron-left"
                    iconBeforeSize="md"
                    [buttonType]="buttonType.IsIcon"
                    (buttonClicked)="prevPageClicked.emit()"
                ></p-button>
                <ul class="p-paginator-list">
                    <li class="p-paginator-item">
                        <p-button
                            *ngIf="!lessBtnDisabled"
                            class="p-paginator-page less"
                            ariaLabel="Show other {{ step }} pages"
                            iconBefore="more-horizontal"
                            iconBeforeSize="md"
                            [buttonType]="buttonType.IsIcon"
                            (buttonClicked)="morePagesClicked(false)"
                        ></p-button>
                    </li>
                    <li class="p-paginator-item" *ngFor="let pageNumber of pageList">
                        <p-button
                            class="p-paginator-page p-paragraph-3"
                            [class.selected]="pageNumber === currentPage"
                            label="{{ pageNumber }}"
                            [ariaLabel]="'Move to page ' + pageNumber"
                            [buttonType]="buttonType.Tertiary"
                            (buttonClicked)="pageNumberClicked.emit(pageNumber)"
                        ></p-button>
                    </li>
                    <li class="p-paginator-item">
                        <p-button
                            *ngIf="!moreBtnDisabled"
                            class="p-paginator-page more"
                            ariaLabel="Show other {{ step }} pages"
                            iconBefore="more-horizontal"
                            iconBeforeSize="md"
                            [buttonType]="buttonType.IsIcon"
                            (buttonClicked)="morePagesClicked(true)"
                        ></p-button>
                    </li>
                </ul>
                <p-button
                    class="p-paginator-arrow next"
                    ariaLabel="Move to the next page"
                    [disabled]="!canMoveToNext"
                    iconBefore="chevron-right"
                    iconBeforeSize="md"
                    [buttonType]="buttonType.IsIcon"
                    (buttonClicked)="nextPageClicked.emit()"
                ></p-button>
                <p-button
                    class="p-paginator-arrow last"
                    ariaLabel="Move to the last page"
                    [disabled]="!canMoveToNext"
                    iconBefore="last"
                    iconBeforeSize="xs"
                    [buttonType]="buttonType.IsIcon"
                    (buttonClicked)="lastPageClicked.emit()"
                ></p-button>

                <p-field-single-select
                    *ngIf="!onGroupingMode"
                    class="p-paginator-select"
                    idField="name"
                    nameField="name"
                    [list]="pageSizeOptions"
                    [value]="pageSize"
                    [canSearch]="false"
                    [hideToolTip]="true"
                    automationLabel="pageSize"
                    (valueChange)="pageSizeChanged.emit($event)"
                >
                </p-field-single-select>
            </div>
            <span class="p-paginator-select-label p-paragraph-3">
                {{ onGroupingMode ? groupsPerPageLabel : itemsPerPageLabel }}
            </span>
            <span class="p-paragraph-3 paginator-right-side">
                {{ 'gridFilters.selectedItemsMessage' | translate: { totalSelected: totalSelected } }}
            </span>

            <span
                *ngIf="!onGroupingMode; else showTotalEntitiesCrossGroups"
                class="p-paginator-items p-paragraph-3"
                automation-id="paginator-grid-show"
            >
                <ng-container *ngIf="isTotalLoading">
                    {{ 'gridFilters.displayItemsMessageLoading' | translate }}
                </ng-container>
                <ng-container *ngIf="!isTotalLoading">
                    {{
                        'gridFilters.displayItemsMessage'
                            | translate: { firstItem: firstItemShown + 1, lastItem: lastItemShown + 1, totalItems: totalItems }
                    }}
                </ng-container>
            </span>
            <ng-template #showTotalEntitiesCrossGroups>
                <span class="p-paginator-items p-paragraph-3" automation-id="paginator-grid-show">
                    {{ 'gridFilters.totalItemsMessage' | translate: { totalItems: totalEntitiesCrossGroups || 0 } }}
                </span>
            </ng-template>

            <p-button
                class="p-paginator-refresh"
                ariaLabel="Refresh"
                [buttonType]="buttonType.IsIcon"
                iconBefore="refresh-cw"
                iconBeforeSize="sm"
                matTooltip="Refresh"
                matTooltipPosition="above"
                (buttonClicked)="reloadRows.emit()"
            ></p-button>
        </div>
    `,
    styleUrls: ['./p-paginator.component.scss']
})
export class PPaginatorComponent implements OnInit, OnChanges {
    private firstInList = 0;
    private lastInList = 0;

    public readonly itemsPerPageLabel = this.translate.instant('gridFilters.itemsPerPage');
    public readonly buttonType = ButtonTypeEnum;
    public readonly step = 5;

    public lessBtnDisabled = true;
    public moreBtnDisabled = false;
    public pageList?: number[];
    public pageSizeOptions: ListItem[] = [];
    public groupsPerPageLabel = '';
    public get canMoveToPrevious(): boolean {
        return this.currentPage > 1;
    }
    public get canMoveToNext(): boolean {
        return this.currentPage < this.totalPages;
    }
    public get isTotalLoading(): boolean {
        return this.totalItems == -1;
    }

    @Input() public currentPage = 1; // based 1
    @Input() public totalPages = 0;
    @Input() public pageSizeValues?: number[];
    @Input() public pageSize = DEFAULT_PAGE_SIZE;
    @Input() public groupingPageSize = DEFAULT_GROUPING_PAGE_SIZE;
    @Input() public totalItems?: number;
    @Input() public totalEntitiesCrossGroups?: number;
    @Input() public firstItemShown = 0;
    @Input() public lastItemShown = 0;
    @Input() public totalSelected?: number;
    @Input() public onGroupingMode = false;
    @Input() public itemsPerPageCustomLabel = '';
    @Output()
    public pageSizeChanged = new EventEmitter<number>();
    @Output()
    public prevPageClicked = new EventEmitter();
    @Output()
    public nextPageClicked = new EventEmitter();
    @Output()
    public firstPageClicked = new EventEmitter();
    @Output()
    public lastPageClicked = new EventEmitter();
    @Output()
    public pageNumberClicked = new EventEmitter<number>();
    @Output()
    public reloadRows = new EventEmitter();

    constructor(private readonly translate: TranslateService) {}

    ngOnInit(): void {
        if (this.currentPage % this.step === 0) {
            this.firstInList = this.currentPage - this.step + 1;
        } else {
            this.firstInList = Math.floor(this.currentPage / this.step) * this.step + 1;
        }
        this.lastInList = this.getLastInList();
        this.moreLessBtnDisabled();
        this.buildPagesList();
        this.initPageSizeOptions();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['totalPages']?.currentValue !== changes['totalPages']?.previousValue) {
            this.definePagesList(false, true);
        } else if (changes['currentPage']?.currentValue !== changes['currentPage']?.previousValue) {
            this.changedPageNumber();
        }
        this.groupsPerPageLabel = `${this.groupingPageSize} ${this.translate.instant('gridFilters.groupsPerPage')}`;
    }

    public morePagesClicked(increasePageNumber: boolean): void {
        this.definePagesList(increasePageNumber, false);
        if (increasePageNumber) {
            this.pageNumberClicked.emit(this.firstInList);
        } else {
            this.pageNumberClicked.emit(this.lastInList);
        }
    }

    private definePagesList(increasePageNumber: boolean, isFromNgOnChanges: boolean): void {
        if (increasePageNumber) {
            this.firstInList += this.step;
            this.lastInList = this.getLastInList();
            if (this.lastInList >= this.totalPages || this.currentPage === this.totalPages) {
                this.lastInList = this.totalPages;
                this.firstInList = this.lastInList < this.step ? 1 : Math.floor((this.lastInList - 1) / this.step) * this.step + 1;
            }
        } else {
            if (!isFromNgOnChanges) {
                this.firstInList -= this.step;
            }
            this.lastInList = this.getLastInList();
            if (this.firstInList <= 1 || this.currentPage === 1) {
                this.firstInList = 1;
                this.lastInList = this.getLastInList();
            }
        }
        this.moreLessBtnDisabled();
        this.buildPagesList();
    }

    private moreLessBtnDisabled(): void {
        this.moreBtnDisabled = this.totalPages <= this.lastInList;
        this.lessBtnDisabled = this.firstInList <= 1;
    }

    private buildPagesList(): void {
        this.pageList = [];
        for (let i = this.firstInList; i <= this.lastInList; i++) {
            this.pageList.push(i);
        }
    }

    private changedPageNumber(): void {
        if (this.currentPage > this.lastInList) {
            this.definePagesList(true, false);
        } else if (this.currentPage < this.firstInList) {
            this.definePagesList(false, false);
        } else if (this.currentPage === this.firstInList || this.currentPage === this.lastInList) {
            this.buildPagesList();
        }
    }

    private getLastInList(): number {
        const last = this.firstInList + this.step - 1;
        return last > this.totalPages ? this.totalPages : last;
    }

    private initPageSizeOptions(): void {
        this.pageSizeOptions = this.pageSizeValues
            ? this.pageSizeValues.map((size, i) => {
                  return { id: i, name: size.toString() };
              })
            : DEFAULT_PAGE_SIZE_OPTIONS;
    }
}
